import React, { useEffect, useRef } from "react";
import MDEditor, { commands } from "@uiw/react-md-editor";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";

interface MarkdownFieldLimitedProps {
  value: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  minHeight?: number;
  maxLength?: number; // legacy/native attr (optional)
  maxCharCount?: number; // new prop to display & enforce hard limit
  label?: string;
  required?: boolean;
  autoFocus?: boolean; // focus textarea on mount
}

const MarkdownFieldLimited: React.FC<MarkdownFieldLimitedProps> = ({
  value,
  onChange,
  placeholder,
  minHeight = 200,
  label,
  maxLength,
  maxCharCount,
  required,
  autoFocus = false,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);

  // Ensure textarea attributes reflect provided limits (useful for native keyboards)
  useEffect(() => {
    const ta = editorRef.current?.querySelector("textarea");
    if (ta) {
      const attr = maxCharCount ?? maxLength;
      if (attr != null) ta.setAttribute("maxLength", String(attr));
      else ta.removeAttribute("maxLength");

      if (required) ta.setAttribute("required", "true");
      else ta.removeAttribute("required");

      if (autoFocus) {
        // defer focus to ensure MDEditor finished mount
        requestAnimationFrame(() => {
          ta.focus();
        });
      }
    }
  }, [maxLength, maxCharCount, required, autoFocus]);

  // Helper to enforce/truncate to maxCharcount on any change source
  const handleChange = (val?: string) => {
    const newVal = val ?? "";
    if (maxCharCount != null && newVal.length > maxCharCount) {
      const truncated = newVal.slice(0, maxCharCount);
      onChange(truncated === "" ? undefined : truncated);
    } else {
      onChange(newVal === "" ? undefined : newVal);
    }
  };

  const newLineCommand = {
    name: "newLine",
    keyCommand: "newLine",
    buttonProps: { "aria-label": "Insert New Line", title: "Insert Line Break" },
    icon: (
      <svg fill="currentColor" viewBox="0 0 24 24" width="16" height="16">
        <path d="M4.64119 12.5L7.51402 15.2038C7.81565 15.4877 7.83004 15.9624 7.54615 16.264C7.26226 16.5657 6.78761 16.58 6.48598 16.2961L2.23598 12.2961C2.08539 12.1544 2 11.9568 2 11.75C2 11.5432 2.08539 11.3456 2.23598 11.2038L6.48598 7.20385C6.78761 6.91996 7.26226 6.93435 7.54615 7.23598C7.83004 7.53761 7.81565 8.01226 7.51402 8.29615L4.64118 11L14.75 11C15.7165 11 16.5 10.2165 16.5 9.25V4.75C16.5 4.33579 16.8358 4 17.25 4C17.6642 4 18 4.33579 18 4.75V9.25C18 11.0449 16.5449 12.5 14.75 12.5L4.64119 12.5Z" />
      </svg>
    ),
    execute: (state: any, api: any) => {
      const { selectedText } = state;
      const modifyText = selectedText ? selectedText + "  \n" : "  \n";
      const newVal = (state.text ?? "").slice(0, state.selection.start) + modifyText + (state.text ?? "").slice(state.selection.end);
      // ensure we enforce maxCharcount
      if (maxCharCount != null && newVal.length > maxCharCount) {
        api.replaceSelection(modifyText.slice(0, Math.max(0, maxCharCount - ((state.text ?? "").length - (state.selection.end - state.selection.start)))));
      } else {
        api.replaceSelection(modifyText);
      }
    },
  };

  // Current char count from the controlled value
  const charCount = (value ?? "").length;

  return (
    <div className="markdown-editor-wrapper relative" data-color-mode="light" ref={editorRef}>
      {label && <div className="headingTxt mb-2">{label}</div>}

      <MDEditor
        value={value}
        onChange={(v) => handleChange(v)}
        preview="edit"
        height={minHeight}
        previewOptions={{
          rehypePlugins: [[rehypeSanitize]],
          remarkPlugins: [remarkGfm],
        }}
        textareaProps={{
          placeholder,
          onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            const textarea = e.currentTarget;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;

            if (e.key === "Enter" && !e.shiftKey) {
              // soft line break: "  \n"
              e.preventDefault();
              const insertion = "  \n";
              const insertionLen = insertion.length;

              // allowed = how many chars we can insert considering we're replacing selection [start,end)
              const allowed = maxCharCount != null ? maxCharCount - ((value ?? "").length - (end - start)) : insertionLen;

              if (allowed <= 0) {
                // nothing allowed — stop input
                return;
              }

              const toInsert = insertionLen <= allowed ? insertion : insertion.slice(0, allowed);
              const newValue = (value ?? "").slice(0, start) + toInsert + (value ?? "").slice(end);

              handleChange(newValue);

              requestAnimationFrame(() => {
                const newPos = start + toInsert.length;
                textarea.selectionStart = textarea.selectionEnd = newPos;
              });
            } else if (e.key === "Enter" && e.shiftKey) {
              // paragraph break: "\n\n"
              e.preventDefault();
              const insertion = "\n\n";
              const insertionLen = insertion.length;

              const allowed = maxCharCount != null ? maxCharCount - ((value ?? "").length - (end - start)) : insertionLen;

              if (allowed <= 0) {
                return;
              }

              const toInsert = insertionLen <= allowed ? insertion : insertion.slice(0, allowed);
              const newValue = (value ?? "").slice(0, start) + toInsert + (value ?? "").slice(end);

              handleChange(newValue);

              requestAnimationFrame(() => {
                const newPos = start + toInsert.length;
                textarea.selectionStart = textarea.selectionEnd = newPos;
              });
            }
          },
        }}
        commands={[
          commands.bold,
          commands.italic,
          commands.strikethrough,
          commands.unorderedListCommand,
          commands.orderedListCommand,
          commands.link,
          newLineCommand,
        ]}
      />

      {/* Character counter (bottom-right). Show only if maxCharcount is provided and there's at least 1 char */}
      {maxCharCount != null && charCount > 0 && (
        <div className="absolute bottom-1 right-2 text-xs text-gray-500">
          {charCount}/{maxCharCount}
        </div>
      )}
    </div>
  );
};

export default MarkdownFieldLimited;


// import React, { useEffect, useRef } from "react";
// import MDEditor, { commands } from "@uiw/react-md-editor";
// import rehypeSanitize from "rehype-sanitize";
// import remarkGfm from "remark-gfm";

// interface MarkdownFieldLimitedProps {
//   value: string;
//   onChange: (value: string | undefined) => void;
//   placeholder?: string;
//   minHeight?: number;
//   maxLength?: number;
//   label?: string;
//   required?: boolean;
// }

// const MarkdownFieldLimited: React.FC<MarkdownFieldLimitedProps> = ({
//   value,
//   onChange,
//   placeholder,
//   minHeight = 200,
//   label,
//   maxLength,
//   required,
// }) => {
//   const editorRef = useRef<HTMLDivElement>(null);

//   useEffect(() => {
//     if (editorRef?.current) {
//       const textarea = editorRef?.current?.querySelector("textarea");
//       if (textarea) {
//         if (maxLength) textarea.setAttribute("maxLength", String(maxLength));
//         if (required) textarea.setAttribute("required", "true");
//       }
//     }
//   }, [maxLength, required]);

//   const newLineCommand = {
//     name: 'newLine',
//     keyCommand: 'newLine',
//     buttonProps: { 'aria-label': 'Insert New Line', title: 'Insert Line Break' },
//     icon: (
//       <svg fill="currentColor" viewBox="0 0 24 24" width="16" height="16">
//         <path d="M4.64119 12.5L7.51402 15.2038C7.81565 15.4877 7.83004 15.9624 7.54615 16.264C7.26226 16.5657 6.78761 16.58 6.48598 16.2961L2.23598 12.2961C2.08539 12.1544 2 11.9568 2 11.75C2 11.5432 2.08539 11.3456 2.23598 11.2038L6.48598 7.20385C6.78761 6.91996 7.26226 6.93435 7.54615 7.23598C7.83004 7.53761 7.81565 8.01226 7.51402 8.29615L4.64118 11L14.75 11C15.7165 11 16.5 10.2165 16.5 9.25V4.75C16.5 4.33579 16.8358 4 17.25 4C17.6642 4 18 4.33579 18 4.75V9.25C18 11.0449 16.5449 12.5 14.75 12.5L4.64119 12.5Z" fill="#212121" />
//       </svg>
//     ),
//     execute: (state: any, api: any) => {
//       const { selectedText, selection } = state;
//       const modifyText = selectedText ? selectedText + '  \n' : '  \n';
//       api.replaceSelection(modifyText);
//     },
//   };

//   return (
//     <div className="markdown-editor-wrapper" data-color-mode="light" ref={editorRef}>
//       {label && <div className="headingTxt mb-2">{label}</div>}
//       <MDEditor
//         value={value}
//         onChange={onChange}
//         preview="edit"
//         height={minHeight}
//         previewOptions={{
//           rehypePlugins: [[rehypeSanitize]],
//           remarkPlugins: [remarkGfm],
//         }}
//         textareaProps={{
//           placeholder,
//           onKeyDown: (e) => {
//             const textarea = e.currentTarget;
//             const start = textarea.selectionStart;
//             const end = textarea.selectionEnd;

//             if (e.key === 'Enter' && !e.shiftKey) {
//               // Override default Enter → soft line break
//               e.preventDefault();
//               const newValue =
//                 value.slice(0, start) + '  \n' + value.slice(end);
//               onChange(newValue);

//               requestAnimationFrame(() => {
//                 textarea.selectionStart = textarea.selectionEnd = start + 3;
//               });
//             } else if (e.key === 'Enter' && e.shiftKey) {
//               // Shift+Enter → normal paragraph
//               e.preventDefault();
//               const newValue =
//                 value.slice(0, start) + '\n\n' + value.slice(end);
//               onChange(newValue);

//               requestAnimationFrame(() => {
//                 textarea.selectionStart = textarea.selectionEnd = start + 2;
//               });
//             }
//           },
//         }}
//         commands={[
//           commands.bold,
//           commands.italic,
//           commands.strikethrough,
//           commands.unorderedListCommand,
//           commands.orderedListCommand,
//           commands.link,
//           newLineCommand
//         ]}
//       />
//     </div>
//   );
// };

// export default MarkdownFieldLimited;